import { formatVoiceLabel, isDisplayableDiarization } from '@app/modules/diarization/speaker-utils';
import { SpeakerDisplay } from '@app/modules/persons/components/SpeakerDisplay';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { forwardRef } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useTranscriptionInteractionsStore } from '../hooks/useTranscriptionInteractionsStore';
import { TranscriptionBlockData } from '../lib/transcription.types';
import { EditableTranscriptionWord } from './EditableTranscriptionWord';
import { TranscriptionWord } from './TranscriptionWord';

const musicSegmentMessage = defineMessage({
  id: 'audit.transcriptionScreen.music',
  defaultMessage: '[Música/Silencio]',
});

type TranscriptionBlockItemProps = {
  block: TranscriptionBlockData;
  blockActive: boolean;
};

/**
 * One transcript row: timestamp, optional speaker line, and words (or music placeholder).
 * The row is clickable to seek to the segment start (YouTube-style block click).
 */
export const TranscriptionBlockItem = forwardRef<HTMLDivElement, TranscriptionBlockItemProps>(
  function TranscriptionBlockItem({ block, blockActive }, ref) {
    const { formatMessage } = useIntl();
    const showWordEfficiency = useTranscriptionInteractionsStore(
      (state) => state.showWordEfficiency,
    );
    const matchWordIds = useTranscriptionInteractionsStore((state) => state.matchWordIds);
    const isEditMode = useTranscriptionInteractionsStore((state) => state.isEditMode);
    const editingWordId = useTranscriptionInteractionsStore((state) => state.editingWordId);
    const editingDraftText = useTranscriptionInteractionsStore((state) => state.editingDraftText);
    const onEditingDraftChange = useTranscriptionInteractionsStore(
      (state) => state.onEditingDraftChange,
    );
    const onWordEditStart = useTranscriptionInteractionsStore((state) => state.onWordEditStart);
    const onWordEditSave = useTranscriptionInteractionsStore((state) => state.onWordEditSave);
    const onWordEditCancel = useTranscriptionInteractionsStore((state) => state.onWordEditCancel);
    const isWordSaving = useTranscriptionInteractionsStore((state) => state.isWordSaving);
    const wordSaveError = useTranscriptionInteractionsStore((state) => state.wordSaveError);
    const onWordClick = useTranscriptionInteractionsStore((state) => state.onWordClick);
    const onSeekSegment = useTranscriptionInteractionsStore((state) => state.onSeekSegment);
    const isWordHighlighted = useTranscriptionInteractionsStore((state) => state.isWordHighlighted);

    const fallbackLabel = formatVoiceLabel(block.speakerLabel) || block.speakerLabel;
    const showSpeaker = isDisplayableDiarization({
      person_id: block.personId,
      voice_label: block.speakerLabel,
    });

    return (
      <Box
        ref={ref}
        onClick={() => onSeekSegment(block)}
        sx={{
          display: 'flex',
          gap: 1.5,
          py: 1,
          px: 1,
          borderRadius: 1,
          bgcolor: blockActive ? 'action.selected' : 'transparent',
          '&:hover': {
            bgcolor: 'action.hover',
          },
          cursor: 'pointer',
          // Offset from list top when auto-scrolling (scrollIntoView block: 'start').
          scrollMarginTop: '50px',
        }}
      >
        <Chip
          color={blockActive ? 'info' : 'default'}
          variant={blockActive ? 'outlined' : 'filled'}
          size="small"
          label={block.timeLabel}
          sx={{
            flexShrink: 0,
            ...(!blockActive && {
              bgcolor: 'action.hover',
              color: 'text.secondary',
              '& .MuiChip-label': { color: 'text.secondary' },
            }),
          }}
        />
        <Stack direction="column" spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          {showSpeaker ? (
            <SpeakerDisplay
              personId={block.personId}
              fallbackLabel={fallbackLabel}
              color={block.speakerColor}
              size="small"
            />
          ) : null}
          {block.isMusic ? (
            <Typography variant="body2" color="text.secondary" component="span">
              {formatMessage(musicSegmentMessage)}
            </Typography>
          ) : (
            <Box sx={{ typography: 'body2' }}>
              {block.words.map((word, index) => (
                <Box
                  component="span"
                  key={word.id}
                  sx={{ display: 'inline', color: blockActive ? 'text.primary' : 'text.secondary' }}
                >
                  {index > 0 ? ' ' : null}
                  {isEditMode && editingWordId === word.id ? (
                    <EditableTranscriptionWord
                      draftText={editingDraftText}
                      onDraftChange={onEditingDraftChange}
                      onSave={onWordEditSave}
                      onCancel={onWordEditCancel}
                      isSaving={isWordSaving}
                    />
                  ) : (
                    <TranscriptionWord
                      text={word.text}
                      confidence={word.confidence}
                      showEfficiency={showWordEfficiency}
                      isHighlighted={isWordHighlighted(word, blockActive)}
                      isSearchMatch={matchWordIds.has(word.id)}
                      isEditMode={isEditMode}
                      onClick={() =>
                        isEditMode
                          ? onWordEditStart(word.id, word.text)
                          : onWordClick(word.start, block.chunkTime)
                      }
                    />
                  )}
                </Box>
              ))}
              {wordSaveError && editingWordId && block.words.some((w) => w.id === editingWordId) ? (
                <Typography
                  variant="caption"
                  color="error"
                  component="span"
                  sx={{ display: 'block' }}
                >
                  {wordSaveError}
                </Typography>
              ) : null}
            </Box>
          )}
        </Stack>
      </Box>
    );
  },
);
